/* Novedades de RRHH: lo que se puede decidir sin tocar la base.
 *
 * Pedido de Nico (25/08/2026): «un sector de novedades. Ahí se dejan registro
 * y documentación de una novedad como enfermedad, suspensión, muerte de un
 * familiar. La idea es que puedas consultar las novedades por empleado».
 *
 * ── NO CONFUNDIR CON `services/novedades.js` ────────────────────────────────
 *
 * Ése es el INICIO del hub, y arranca diciendo que NO HAY una tabla
 * `novedades` porque sus tarjetas se DERIVAN de datos que ya existen —nadie
 * escribe novedades a mano y una pantalla que hay que alimentar se muere a la
 * semana—. Esto es lo contrario, y por eso puede existir: que a alguien se le
 * murió el padre no lo sabe ningún sistema. No hay de dónde derivarlo.
 *
 * Acá vive lo puro; la lectura y la escritura están en
 * `services/rrhhNovedades.js`. Separado para poder probar la parte que decide
 * sin levantar un cliente de Supabase.
 */

export const BUCKET = "novedades";

/* Los tres que pidió Nico, más una salida.
 *
 * `otra` existe para que nadie tenga que forzar una novedad dentro de un tipo
 * que no es: el detalle la explica. Sin esa salida, lo que no encaja se carga
 * mal —y ensucia el conteo— o directamente no se carga. */
export const TIPOS = [
  { id: "enfermedad", label: "Enfermedad" },
  { id: "suspension", label: "Suspensión" },
  { id: "duelo", label: "Fallecimiento de un familiar" },
  // Pedido de Nico (P-170): un accidente que entra por la ART se marca acá, y
  // así se distingue del que no. Es un tipo propio y no un «otra» con texto
  // porque es justo lo que se quiere poder contar y filtrar aparte.
  { id: "art", label: "Accidente / ART" },
  { id: "otra", label: "Otra" },
];

/* El rótulo que se lee. Un tipo desconocido —uno viejo, o cargado por otra
 * puerta— se muestra tal cual y no se traga: «no lo conozco» no es «no hay». */
export const rotuloTipo = (t) =>
  TIPOS.find((x) => x.id === t)?.label || t || "—";

/* ── LA LÍNEA DE TIEMPO, QUE ES LO QUE PIDIÓ NICO ────────────────────────────
 *
 * «Consultar las novedades por empleado» incluye las licencias: para quien
 * mira la ficha, un parte de enfermo ES una novedad. Pero las licencias NO se
 * copian a `rrhh_novedades` —se leen de su tabla y se muestran juntas—, porque
 * duplicarlas obligaría a cargarlas dos veces o a sincronizarlas, y lo segundo
 * se rompe en silencio el día que alguien anula una de un lado solo.
 *
 * Cada ítem dice de dónde vino. Importa: «anular» sólo se ofrece sobre lo que
 * esta pantalla creó — una licencia se anula en Licencias, que es donde está
 * su historia. Y los `id` de las dos tablas se pisan (las dos arrancan en 1),
 * así que nada puede indexarse sólo por `id`.
 */
export function lineaDeTiempo(novedades, licencias) {
  const a = (novedades || []).map((n) => ({
    origen: "novedad",
    id: n.id,
    clave: `novedad-${n.id}`,
    fecha: n.desde,
    hasta: n.hasta,
    tipo: n.tipo,
    rotulo: rotuloTipo(n.tipo),
    // `detalle` acá y `nota` allá. Sin normalizarlo, media lista se vería
    // sin texto y parecería que nadie escribió nada.
    detalle: n.detalle,
    quien: n.usuario_nombre || n.usuario,
    anulado: !!n.anulado_en,
    fila: n,
  }));
  const b = (licencias || []).map((l) => ({
    origen: "licencia",
    id: l.id,
    clave: `licencia-${l.id}`,
    fecha: l.desde,
    hasta: l.hasta,
    tipo: l.tipo,
    rotulo: l.tipo,
    detalle: l.nota,
    quien: l.usuario_nombre || l.usuario,
    anulado: !!l.anulado_en,
    fila: l,
  }));
  return [...a, ...b].sort((x, y) =>
    String(y.fecha ?? "").localeCompare(String(x.fecha ?? "")));
}

/* La ruta del adjunto dentro del bucket.
 *
 * Cuelga de la novedad y lleva un sufijo al azar: dos personas suben
 * "certificado.pdf" el mismo día y sin esto el segundo pisa al primero, sin
 * que nadie se entere hasta que hace falta el primero.
 *
 * El nombre con el que llegó NO va en la ruta: va en la fila, que es donde se
 * lo puede leer sin adivinar. */
export function rutaDe(novedadId, nombre) {
  const ext = (String(nombre || "").match(/\.([A-Za-z0-9]{1,8})$/)?.[1] || "bin")
    .toLowerCase();
  const azar = Math.random().toString(36).slice(2, 10);
  return `${novedadId}/${Date.now()}-${azar}.${ext}`;
}
